import React, { useState, useEffect, useRef } from 'react';
import { Copy, Check, BarChart } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения результатов генерации случайных чисел
 */
const ResultsDisplay = ({ results, formData, showStatistics, setShowStatistics }) => {
  const [copied, setCopied] = useState(false);
  const [animatingIndices, setAnimatingIndices] = useState([]);
  const resultsContainerRef = useRef(null);

  // Форматирование результатов в зависимости от выбранного формата
  const formatResults = () => {
    switch (formData.format) {
      case 'comma':
        return results.join(', ');
      case 'newline':
        return results.join('\n');
      case 'space':
        return results.join(' ');
      default:
        return results.join(', ');
    }
  };

  // Копирование результатов в буфер обмена
  const copyToClipboard = () => {
    navigator.clipboard.writeText(formatResults()).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      (err) => {
        console.error('Не удалось скопировать текст: ', err);
      }
    );
  };

  // Анимация чисел при первом отображении результатов
  useEffect(() => {
    if (results.length > 0) {
      const indices = [];
      for (let i = 0; i < results.length; i++) {
        indices.push(i);
        
        // Если чисел слишком много, анимируем только часть для производительности
        if (i > 100) break;
      }
      setAnimatingIndices(indices);
      
      // Сбрасываем анимацию через некоторое время
      const timer = setTimeout(() => {
        setAnimatingIndices([]);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [results]);

  // Если нет результатов, не отображаем компонент
  if (!results || results.length === 0) {
    return null;
  }

  // Определяем стиль для отображения чисел в зависимости от их количества
  const getResultItemStyle = () => {
    if (results.length <= 10) return 'text-3xl font-bold';
    if (results.length <= 30) return 'text-2xl font-bold';
    if (results.length <= 100) return 'text-xl font-semibold';
    if (results.length <= 500) return 'text-base font-medium';
    return 'text-sm';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8 relative overflow-hidden">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <div className="w-8 h-8 flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/50 rounded-full text-indigo-600 dark:text-indigo-400 mr-2">
            {results.length}
          </div>
          Сгенерированные числа
        </h3>
        
        <div className="flex space-x-2">
          <Button
            onClick={() => setShowStatistics(!showStatistics)}
            size="sm"
            variant="outline"
            icon={<BarChart className="w-4 h-4" />}
          >
            {showStatistics ? 'Скрыть статистику' : 'Показать статистику'}
          </Button>
          
          <Button
            onClick={copyToClipboard}
            size="sm"
            variant="outline"
            icon={copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
          >
            {copied ? 'Скопировано!' : 'Копировать'}
          </Button>
        </div>
      </div>

      {/* Информация о параметрах генерации */}
      <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 mb-4 text-sm text-gray-600 dark:text-gray-400 flex flex-wrap gap-2">
        <div>
          <span className="font-medium">Диапазон:</span> {formData.customRange ? 'Пользовательский' : `${formData.min} - ${formData.max}`}
        </div>
        <div>•</div>
        <div>
          <span className="font-medium">Количество:</span> {formData.count}
        </div>
        <div>•</div>
        <div>
          <span className="font-medium">Уникальные:</span> {formData.unique ? 'Да' : 'Нет'}
        </div>
        {formData.sorting !== 'none' && (
          <>
            <div>•</div>
            <div>
              <span className="font-medium">Сортировка:</span> {formData.sorting === 'asc' ? 'По возрастанию' : 'По убыванию'}
            </div>
          </>
        )}
        {formData.distribution !== 'uniform' && (
          <>
            <div>•</div>
            <div>
              <span className="font-medium">Распределение:</span> {
                formData.distribution === 'normal' ? 'Нормальное' : 
                formData.distribution === 'weighted' ? 'Взвешенное' : 'Равномерное'
              }
            </div>
          </>
        )}
        {parseInt(formData.decimalPlaces) > 0 && (
          <>
            <div>•</div>
            <div>
              <span className="font-medium">Дес. знаков:</span> {formData.decimalPlaces}
            </div>
          </>
        )}
      </div>

      {/* Контейнер для отображения результатов */}
      <div 
        ref={resultsContainerRef}
        className={`
          bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700 
          p-4 max-h-[300px] overflow-auto relative
          ${formData.format === 'newline' ? 'whitespace-pre-line' : ''}
        `}
      >
        {/* Маленькое количество чисел - показываем каждое число с анимацией */}
        {results.length <= 100 ? (
          <div className="flex flex-wrap gap-2">
            {results.map((num, index) => (
              <div 
                key={`result-${index}`}
                className={`
                  ${getResultItemStyle()} 
                  bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg 
                  border border-gray-200 dark:border-gray-700 shadow-sm
                  ${animatingIndices.includes(index) ? 'bubble-animation' : ''}
                `}
                style={{
                  animationDelay: `${index * 50}ms`,
                  color: getColorForNumber(num, formData.min, formData.max)
                }}
              >
                {num}
              </div>
            ))}
          </div>
        ) : (
          // Много чисел - просто текстовое представление
          <div 
            className={`text-gray-800 dark:text-gray-200 font-mono ${results.length > 1000 ? 'text-xs' : 'text-sm'}`}
          >
            {formatResults()}
          </div>
        )}
      </div>

      {/* Дополнительная информация для больших наборов */}
      {results.length > 100 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Показано {results.length} чисел. Для подробного анализа используйте кнопку "Показать статистику".
        </p>
      )}
    </div>
  );
};

// Функция для получения цвета в зависимости от значения числа
const getColorForNumber = (num, min, max) => {
  // Если min и max не указаны или они равны, используем стандартный цвет
  if (!min || !max || min === max) {
    return 'inherit';
  }
  
  // Преобразуем значения в числа
  min = parseFloat(min);
  max = parseFloat(max);
  
  // Определяем положение числа в диапазоне (от 0 до 1)
  const range = max - min;
  const position = (num - min) / range;
  
  // Градиент от синего (холодный) к красному (теплый)
  if (position < 0.2) {
    return 'rgb(59, 130, 246)'; // Синий
  } else if (position < 0.4) {
    return 'rgb(16, 185, 129)'; // Зеленый
  } else if (position < 0.6) {
    return 'rgb(139, 92, 246)'; // Фиолетовый
  } else if (position < 0.8) {
    return 'rgb(245, 158, 11)'; // Оранжевый
  } else {
    return 'rgb(239, 68, 68)'; // Красный
  }
};

export default ResultsDisplay;